import React from 'react';
import { X, ShieldCheck } from 'lucide-react';
import { useShop } from '../context/ShopContext';

export const LegalModal: React.FC = () => {
  const { legalModalType, setLegalModalType, locale, shippingConfig } = useShop();

  if (!legalModalType) return null;

  const close = () => setLegalModalType(null);

  const getTitle = () => {
    switch (legalModalType) {
      case 'cgv':
        return locale === 'de' ? 'Allgemeine Geschäftsbedingungen (AGB)' : 'Conditions Générales de Vente (CGV)';
      case 'privacy':
        return locale === 'de' ? 'Datenschutzerklärung' : 'Politique de Confidentialité';
      case 'cookies':
        return locale === 'de' ? 'Cookie-Richtlinie' : 'Gestion des Cookies';
      case 'shipping':
        return locale === 'de' ? 'Versand & Rückgabe' : 'Livraison & Retours';
      case 'withdrawal':
        return locale === 'de' ? 'Widerrufsrecht & Formular' : 'Droit de Rétractation & Formulaire';
      case 'legal':
      default:
        return locale === 'de' ? 'Impressum & Rechtliche Hinweise' : 'Mentions Légales & Coordonnées';
    }
  };

  const renderContent = () => {
    if (locale === 'de') {
      switch (legalModalType) {
        case 'cgv':
          return (
            <div className="space-y-4 text-xs leading-relaxed text-gray-700">
              <p className="font-bold">1. Geltungsbereich</p>
              <p>Diese Allgemeinen Geschäftsbedingungen gelten für alle Bestellungen über den Online-Shop Atelier Subversif. Durch die Bestellung akzeptiert der Kunde diese Bedingungen vollumfänglich.</p>
              <p className="font-bold">2. Preise und Bezahlung</p>
              <p>Alle Preise verstehen sich in CHF für Kunden in der Schweiz und in EUR für sonstige Regionen. Die Bezahlung erfolgt über den zertifizierten Zahlungsanbieter Stripe mittels Kreditkarte oder sonstigen freigeschalteten Zahlungsmethoden.</p>
              <p className="font-bold">3. Lieferung und Eigentumsvorbehalt</p>
              <p>Die Ware bleibt bis zur vollständigen Bezahlung unser Eigentum. Lieferzeiten richten sich nach den angegebenen Fristen.</p>
            </div>
          );
        case 'privacy':
          return (
            <div className="space-y-4 text-xs leading-relaxed text-gray-700">
              <p className="font-bold">1. Erhebung und Verarbeitung personenbezogener Daten</p>
              <p>Wir erheben personenbezogene Daten ausschließlich zur Bearbeitung und Zustellung Ihrer Bestellung. Es erfolgt keinerlei Weitergabe oder Verkauf von Daten an Dritte zu Werbezwecken.</p>
              <p className="font-bold">2. Zahlungsverkehr</p>
              <p>Zahlungsdaten werden verschlüsselt direkt über Stripe verarbeitet. Wir speichern zu keinem Zeitpunkt vollständige Kreditkartennummern auf unseren Servern.</p>
              <p className="font-bold">3. Ihre Rechte</p>
              <p>Sie haben jederzeit das Recht auf Auskunft, Berichtigung oder Löschung Ihrer gespeicherten personenbezogenen Daten.</p>
            </div>
          );
        case 'cookies':
          return (
            <div className="space-y-4 text-xs leading-relaxed text-gray-700">
              <p className="font-bold">Nutzung von Cookies</p>
              <p>Dieser Shop verwendet ausschließlich technisch zwingend notwendige Cookies (Warenkorbspeicherung, Sitzungsverwaltung). Es werden keine Werbe- oder Drittanbieter-Tracking-Cookies eingesetzt.</p>
            </div>
          );
        case 'shipping':
          return (
            <div className="space-y-4 text-xs leading-relaxed text-gray-700">
              <p className="font-bold">Versandinformationen</p>
              <p>Der Versand erfolgt in neutralen und transportsicheren Kartons. Lieferungen in die Schweiz erfolgen in der Regel innerhalb von 2 bis 4 Werktagen. EU-Lieferungen benötigen 4 bis 8 Werktage.</p>
              <p className="font-bold">Rücksendungen</p>
              <p>Rücksendungen unbenutzter und unbeschädigter Artikel in Originalverpackung sind innerhalb von 14 Tagen nach Erhalt möglich.</p>
            </div>
          );
        case 'withdrawal':
          return (
            <div className="space-y-4 text-xs leading-relaxed text-gray-700">
              <p className="font-bold">Widerrufsbelehrung</p>
              <p>Verbraucher haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen. Die Frist beträgt 14 Tage ab dem Tag, an dem Sie die Ware in Besitz genommen haben.</p>
            </div>
          );
        case 'legal':
        default:
          return (
            <div className="space-y-4 text-xs leading-relaxed text-gray-700">
              <p className="font-bold">Atelier Subversif Sàrl</p>
              <p>Rue de Bourg 15, 1003 Lausanne, Schweiz</p>
              <p>E-Mail: contact@subversif-atelier.ch</p>
              <p>Unternehmens-Identifikationsnummer (UID): CHE-384.921.847 TVA</p>
            </div>
          );
      }
    }

    // French
    switch (legalModalType) {
      case 'cgv':
        return (
          <div className="space-y-4 text-xs leading-relaxed text-gray-700">
            <p className="font-bold">1. Champ d'application</p>
            <p>Les présentes Conditions Générales de Vente s'appliquent à toute commande passée sur la boutique en ligne L'Atelier Subversif. Toute commande implique l'adhésion entière de l'acheteur aux présentes conditions.</p>
            <p className="font-bold">2. Prix et Modalités de Paiement</p>
            <p>Les prix sont indiqués en Francs Suisses (CHF) pour la Suisse et en Euros (EUR) pour les autres pays. Le règlement s'effectue au comptant lors de la commande par carte bancaire via le protocole sécurisé Stripe.</p>
            <p className="font-bold">3. Transfert de propriété et expédition</p>
            <p>Les produits demeurent la propriété du vendeur jusqu'au complet paiement du prix. L'expédition intervient dès validation du paiement.</p>
          </div>
        );
      case 'privacy':
        return (
          <div className="space-y-4 text-xs leading-relaxed text-gray-700">
            <p className="font-bold">1. Données collectées</p>
            <p>Nous collectons uniquement les données indispensables au traitement et à l'acheminement de vos commandes (nom, adresse postale, e-mail). Aucune donnée n'est cédée ou vendue à des tiers.</p>
            <p className="font-bold">2. Sécurité des transactions</p>
            <p>Les informations bancaires sont chiffrées de bout en bout et traitées directement par Stripe. Aucun numéro de carte ne transite ni n'est conservé sur notre infrastructure.</p>
            <p className="font-bold">3. Vos droits RGPD / LPD</p>
            <p>Vous disposez d'un droit d'accès, de rectification et d'effacement de vos données personnelles par simple demande écrite à contact@subversif-atelier.ch.</p>
          </div>
        );
      case 'cookies':
        return (
          <div className="space-y-4 text-xs leading-relaxed text-gray-700">
            <p className="font-bold">Gestion des Cookies</p>
            <p>Notre boutique utilise exclusivement des cookies strictement indispensables au fonctionnement technique du site (mémorisation de votre panier et sécurisation de la session). Nous n'utilisons aucun traceur publicitaire ou invasif.</p>
          </div>
        );
      case 'shipping':
        return (
          <div className="space-y-4 text-xs leading-relaxed text-gray-700">
            <p className="font-bold">Expédition & Conditionnement discret</p>
            <p>Toutes nos commandes sont conditionnées dans des cartons d'expédition neutres et renforcés, protégeant la céramique contre tout choc de transport. Aucune mention du message secret n'apparaît sur l'extérieur du colis.</p>
            <p className="font-bold">Politique de retours</p>
            <p>Vous disposez d'un délai de 14 jours pour nous retourner tout article intact dans son emballage d'origine pour échange ou remboursement.</p>
          </div>
        );
      case 'withdrawal':
        return (
          <div className="space-y-4 text-xs leading-relaxed text-gray-700">
            <p className="font-bold">Droit légal de rétractation</p>
            <p>Conformément à la législation en vigueur, vous disposez d'un délai de 14 jours francs à compter de la réception de vos articles pour exercer votre droit de rétractation sans avoir à justifier de motifs ni à payer de pénalités.</p>
          </div>
        );
      case 'legal':
      default:
        return (
          <div className="space-y-4 text-xs leading-relaxed text-gray-700">
            <p className="font-bold">Éditeur du site</p>
            <p>Atelier Subversif Sàrl — Société à responsabilité limitée</p>
            <p>Siège social : Rue de Bourg 15, 1003 Lausanne, Suisse</p>
            <p>Contact : contact@subversif-atelier.ch</p>
            <p>Registre du commerce / Numéro IDE : CHE-384.921.847 TVA</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-neutral-950/60 backdrop-blur-xs">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 relative">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-gray-900" />
            <h3 className="font-display font-bold text-base text-gray-900">{getTitle()}</h3>
          </div>
          <button
            onClick={close}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto pr-2">
          {renderContent()}
        </div>

        <div className="mt-5 pt-3 border-t border-gray-100 text-right">
          <button
            onClick={close}
            className="px-4 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold hover:bg-pink-600 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};
